import { useEffect, useRef, useState } from "react";
import styles from "./EditClient.module.scss";
import InputCustom from "../../../../../components/InputCustom/InputCustom";
import Button from "../../../../../components/Button/Button";
import { updateClient } from "../../../../../api/client";
import { fetchAddressSuggestions } from "../../../../../api/address";
import { useFormik } from "formik";
import validationSchema from "../../../../../models/ClientSchema";

function debounce(func, delay) {
  let timer;
  return function (...args) {
    const context = this;
    clearTimeout(timer);
    timer = setTimeout(() => func.apply(context, args), delay);
  };
}

function EditClient({ clientToEdit, onCancelEdit, setClients }) {
  const [errorMessage, setErrorMessage] = useState("");
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const addressRef = useRef();

  const formik = useFormik({
    initialValues: {
      firstName: clientToEdit.first_name || "",
      lastName: clientToEdit.last_name || "",
      email: clientToEdit.email || "",
      phoneNumber: clientToEdit.phone_number || "",
      address: clientToEdit.address || "",
    },
    validationSchema,
    onSubmit: async (values) => {
      try {
        if (!values.firstName.trim()) {
          setErrorMessage("Le prénom ne peut pas être vide");
          return;
        }
        if (!values.lastName.trim()) {
          setErrorMessage("Le nom ne peut pas être vide");
          return;
        }
        if (!values.email.trim()) {
          setErrorMessage("L'email ne peut pas être vide");
          return;
        }

        const payload = {
          firstName: values.firstName,
          lastName: values.lastName,
          email: values.email,
          phoneNumber: values.phoneNumber || "",
          address: values.address || "",
        };

        const updatedClient = await updateClient(clientToEdit.id, payload);

        setClients((prevClients) => {
          const index = prevClients.findIndex((c) => c.id === updatedClient.id);
          if (index !== -1) {
            const updatedClients = [...prevClients];
            updatedClients[index] = updatedClient;
            return updatedClients;
          }
          return prevClients;
        });

        onCancelEdit();
      } catch (error) {
        console.error("Erreur lors de la modification du client : ", error);
        setErrorMessage(
          error.response?.data?.message ||
            "Une erreur est survenue lors de la modification du client",
        );
      }
    },
  });

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (addressRef.current && !addressRef.current.contains(event.target)) {
        setAddressSuggestions([]);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const fetchAddressSuggestionsDebouncedRef = useRef(
    debounce(async (inputText) => {
      try {
        const suggestions = await fetchAddressSuggestions(inputText);
        setAddressSuggestions(suggestions);
      } catch (error) {
        console.error(
          "Erreur lors de la récupération des suggestions d'adresse : ",
          error,
        );
        setErrorMessage(
          "Erreur lors de la récupération des suggestions d'adresse.",
        );
      }
    }, 500),
  );

  const selectAddress = (address) => {
    formik.setFieldValue("address", address);
    setAddressSuggestions([]);
  };

  const handleCancelModification = () => {
    onCancelEdit();
  };

  return (
    <div className={styles.container}>
      <h2 className={styles.title}>Modification du client</h2>

      {errorMessage && (
        <div className={styles.errorMessage}>{errorMessage}</div>
      )}

      <form onSubmit={formik.handleSubmit}>
        <div className={styles.clientFieldContainer}>
          <h3>Nom</h3>
          <InputCustom
            id="lastName"
            type="text"
            name="lastName"
            value={formik.values.lastName}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
          />
          {formik.touched.lastName && formik.errors.lastName ? (
            <div className={styles.error}>{formik.errors.lastName}</div>
          ) : null}
        </div>

        <div className={styles.clientFieldContainer}>
          <h3>Prénom</h3>
          <InputCustom
            id="firstName"
            type="text"
            name="firstName"
            value={formik.values.firstName}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
          />
          {formik.touched.firstName && formik.errors.firstName ? (
            <div className={styles.error}>{formik.errors.firstName}</div>
          ) : null}
        </div>

        <div className={styles.clientFieldContainer}>
          <h3>Email</h3>
          <InputCustom
            id="email"
            type="text"
            name="email"
            value={formik.values.email}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
          />
          {formik.touched.email && formik.errors.email ? (
            <div className={styles.error}>{formik.errors.email}</div>
          ) : null}
        </div>

        <div className={styles.clientFieldContainer}>
          <h3>Téléphone</h3>
          <InputCustom
            id="phoneNumber"
            type="tel"
            name="phoneNumber"
            value={formik.values.phoneNumber}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
          />
          {formik.touched.phoneNumber && formik.errors.phoneNumber ? (
            <div className={styles.error}>{formik.errors.phoneNumber}</div>
          ) : null}
        </div>

        <div className={styles.autocompleteContainer} ref={addressRef}>
          <h3>Adresse</h3>
          <InputCustom
            id="address"
            type="text"
            name="address"
            placeholder="3 rue de l'exemple, 59300 Valenciennes"
            value={formik.values.address}
            onChange={(e) => {
              const inputValue = e.target.value;
              formik.handleChange(e);
              if (inputValue.length >= 6) {
                fetchAddressSuggestionsDebouncedRef.current(inputValue);
              } else {
                setAddressSuggestions([]);
              }
            }}
            onBlur={formik.handleBlur}
          />
          {formik.touched.address && formik.errors.address ? (
            <div className={styles.error}>{formik.errors.address}</div>
          ) : null}
          {addressSuggestions.length > 0 && (
            <div className={styles.dropdownMenu}>
              {addressSuggestions.map((suggestion) => (
                <div
                  key={suggestion.osm_id}
                  className={styles.dropdownItem}
                  onClick={() => selectAddress(suggestion.address)}
                >
                  {suggestion.address}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className={styles.btnContainer}>
          <Button
            type="submit"
            style={{
              padding: "7px 15px",
              marginRight: "5px",
              fontSize: "0.8rem",
            }}
            color={"var(--secondary-color)"}
          >
            Enregistrer les modifications
          </Button>
          <Button
            type="button"
            style={{
              padding: "7px 15px",
              marginRight: "5px",
              fontSize: "0.8rem",
            }}
            color={"var(--secondary-color)"}
            onClick={handleCancelModification}
          >
            Annuler
          </Button>
        </div>
      </form>
    </div>
  );
}

export default EditClient;
